import React, { FC, useContext, useEffect, useState, ChangeEvent } from 'react';

import {
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow
} from '@material-ui/core';

import { GlobalContext } from '../../../../store';

import {
  useAssignResultsState,
  TTeamFormatPrefix
} from '../../../admin/EventWizard/Common/AssignResults/Context/AssignResults.context';
import { useGetRoundLabel } from '../../../common/Hooks/useGetroundLabel';
import { useEventLeaderboardsState } from '../Context/EventLeaderboards.context';
import RoundScoreSummary from './Component/RoundScoreSummary/RoundScoreSummary';
import { useLeaderboardsResults } from '../../../common/Hooks/useLeaderboardsResults';
import { displayPosition } from '../../../../contrib/Utils/displayPosition';
import { displayScore } from '../../../../contrib/Utils/displayScore';
import { getTeamGameIndex } from '../../../../contrib/Utils/getTeamGameIndex';
import { useRouteInfoState } from '../../../../components/RouteInfo/Context/RouteInfo.context';

interface IProps {
  roundId: number | undefined;
  teamFormat?: TTeamFormatPrefix;
}

export const TeamFormatTable: FC<IProps> = props => {
  const { roundId, teamFormat } = props;
  const {
    actions: {
      adminTeamHoleResults: { get: getAdminTeamHoleResults }
    },
    events,
    adminTeamResults,
    adminTeamHoleResults
  } = useContext(GlobalContext);

  const { eventId } = useRouteInfoState();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(200);

  const assignResultsState = useAssignResultsState();

  const { selectedScoreType, selectedRoundFormat } = assignResultsState;

  const selectedTeamFormat = teamFormat || assignResultsState.selectedTeamFormat;

  const { selectedRound, selectedFlightId, isEventSelected } = useEventLeaderboardsState();

  const handleChangePage = (_: any, pageNumber: number) => {
    setPage(pageNumber);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPage(0);
    setRowsPerPage(Number(event.target.value));
  };

  const [formatIsStableford, setFormatIsStableford] = useState(false);

  useEffect(() => {
    if (selectedRoundFormat) {
      setFormatIsStableford(selectedRoundFormat.scoringFormat === 1);
    }
  }, [selectedRoundFormat]);

  const [sortedResults, formattedTeamsHoleResults] = useLeaderboardsResults({
    adminTeamHoleResults,
    adminTeamResults,
    formatIsStableford,
    roundId: isEventSelected ? Number(eventId) : Number(roundId),
    selectedFlightId,
    selectedScoreType,
    selectedTeamFormat
  });

  const columnNumber = [0, 1, 2, 3];

  const [getRoundLabel] = useGetRoundLabel(events, Number(eventId), Number(roundId));

  const [collapsedRow, setCollapsedRow] = useState<null | number>(null);

  const onClick = (index: number) => () => setCollapsedRow(collapsedRow === index ? null : index);

  useEffect(() => {
    setCollapsedRow(null);
  }, [selectedScoreType, selectedFlightId]);

  useEffect(() => {
    if (roundId) {
      getAdminTeamHoleResults(roundId, true);
    }
  }, [roundId]);

  return (
    <div className="event-leaderboards-table">
      <Table className="leaderboard-score-table">
        <colgroup>
          {columnNumber.map(index => (
            <col key={index} />
          ))}
        </colgroup>

        <TableHead className="leaderboard__thead">
          <TableRow className="leaderboard-header">
            <TableCell className="leaderboard-header__cell override index">Pos</TableCell>

            <TableCell className="leaderboard-header__cell override playerName">TEAM</TableCell>

            <TableCell
              className={`leaderboard-header__cell override result ${!selectedRound ? 'rpad' : ''}`}
            >
              {getRoundLabel()}
            </TableCell>

            <TableCell className="leaderboard-header__cell override through">THRU</TableCell>
          </TableRow>
        </TableHead>

        <TableBody className="leaderboard__tbody">
          {sortedResults && sortedResults.length > 0 ? (
            sortedResults
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((teamResult, index: number) => [
                <TableRow
                  id={`clickableRowResult-${index}`}
                  key={`tableRowResult-${index}`}
                  onClick={onClick(index)}
                >
                  <TableCell className="index">
                    {isEventSelected && teamResult.cut
                      ? 'CUT'
                      : displayPosition(teamResult, selectedTeamFormat, selectedScoreType)}
                  </TableCell>

                  <TableCell className="playerName">{teamResult.teamName}</TableCell>

                  <TableCell className="result">
                    {displayScore(teamResult, selectedTeamFormat, selectedScoreType)}
                  </TableCell>

                  <TableCell className="through">{teamResult.through}</TableCell>
                </TableRow>,
                // TODO-SCRAMBLE: improve this. Hiding this is a temporary solution for scramble events
                !isEventSelected ? (
                  <TableRow className="expandable-row" key={`hiddenTableRow-${index}`}>
                    <TableCell
                      className="inner-cell"
                      colSpan={selectedRound ? 5 : 4}
                      classes={{ root: 'leaderboard-row-event-summary' }}
                    >
                      <Collapse
                        className={`collapse-${index}`}
                        classes={{
                          container: 'leaderboard-collapse',
                          wrapperInner: 'leaderboard-collapse__wrapper-inner override'
                        }}
                        in={collapsedRow === index}
                        timeout="auto"
                        unmountOnExit={true}
                      >
                        {selectedScoreType && (
                          <RoundScoreSummary
                            itemHandicap={getTeamGameIndex(
                              events,
                              eventId,
                              String(roundId),
                              teamResult.groupId,
                              teamResult.teamId
                            )}
                            playerScores={
                              formattedTeamsHoleResults
                                ? formattedTeamsHoleResults[teamResult.teamGameId]
                                : undefined
                            }
                            range={{ start: 0, length: 18 }}
                            scoreType={selectedScoreType}
                            hideHandicapsRow={true}
                            // TODO-SCRAMBLE: improve this. Hiding this is a temporary solution for scramble events
                            hideIndexRow={true}
                          />
                        )}
                      </Collapse>
                    </TableCell>
                  </TableRow>
                ) : (
                  <></>
                )
              ])
          ) : (
            <TableRow>
              <TableCell colSpan={4} className="no-results">
                No results to show
              </TableCell>
            </TableRow>
          )}
        </TableBody>

        <TableFooter>
          <TablePagination
            rowsPerPageOptions={[200, 250, 300]}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            page={page}
            rowsPerPage={rowsPerPage}
            onChangePage={handleChangePage}
            count={sortedResults.length}
          />
        </TableFooter>
      </Table>
    </div>
  );
};
